import React from "react";
import Picky from "ikizmet-react-picky";
import PropTypes from "prop-types";
import useFilterTracking from "../../shared/tracking/useFilterTracking";
import "ikizmet-react-picky/dist/picky.css"; // Include CSS
import "./multiSelectDropdown.scss";

/**
 * Wrapper around `ikizmet-react-picky` (`Picky`) that powers most filter dropdowns
 * across the app. Defaults to multi-select with a search box; pass overrides
 * for single-select usage (e.g. `StartFromFilter`, `FirstContactTypeFilter`).
 *
 * Props:
 *
 *   options                   Array of selectable items. Can be strings ("Network", "Region")
 *                             or objects ({ id, name }). When objects, the keys are configured
 *                             by `valueKey` / `labelKey` (defaults: id / name).
 *   selectedFilters           Current selection. Array for multi-select; single string/object
 *                             for single-select (when `multiple={false}`).
 *   onSelectChange            Required. Callback invoked with the new selection. For multi-select
 *                             receives an array; for single-select receives the single value.
 *   haveOnSelectChange        When true, skips the "empty array / max-exceeded" guard inside
 *                             onChange and always forwards the value. Use when the consumer
 *                             needs to react to deselect-all or exceed-max events itself.
 *   enableMax                 Enables the `maxSelect` cap.
 *   maxSelect                 Max number of selected items allowed when `enableMax` is true.
 *   avoidSelectIfMaxSelected  With `enableMax`, swallows attempts to select beyond `maxSelect`
 *                             instead of letting Picky temporarily exceed the cap.
 *   selectAllText             Label for the "Select All" row when `includeSelectAll` is true.
 *   placeholder               Placeholder shown when nothing is selected.
 *   valueKey                  Object key used as the unique value (default "id").
 *   labelKey                  Object key used as the display label (default "name").
 *   multiple                  Selection mode. true (default) → checkbox list, returns an array.
 *                             false → radio list, returns a single value. Critical for downstream
 *                             reducers/sagas — keep aligned with the consumer's state shape.
 *   keepOpen                  Keeps the dropdown panel open after a selection (default true).
 *                             Set to false for single-select filters so the panel closes
 *                             immediately after a click.
 *   includeSelectAll          Renders a "Select All" row above the options.
 *   includeFilter             Renders a search/filter input at the top of the dropdown
 *                             (default true). Set false when the option list is short.
 *   filterPlaceholder         Placeholder text for the search input (default "Search...").
 *   manySelectedPlaceholder   Trigger label template when multiple items are selected
 *                             (default "%s selected"). `%s` is replaced with the count.
 *   allSelectedPlaceholder    Trigger label template when every option is selected
 *                             (default "%s selected").
 *   height                    Dropdown panel max height in px (default 500).
 *   numberDisplayed           How many selected items to spell out in the trigger before
 *                             collapsing to "N selected". 0 = always show the count summary.
 *   enableOnly                Renders an "Only" link next to each option that selects just
 *                             that one. Adds the `withOnly` className for styling.
 *   enableOnlyText            Override for the "Only" link label.
 *   render                    Optional render-prop forwarded to Picky for fully custom
 *                             option markup. Use sparingly — defeats the consistent styling.
 *   filterName                Explicit Mixpanel filter label override. Auto-detected from DOM when omitted.
 *   moduleName                Explicit Mixpanel module-name override. Auto-detected from the enclosing
 *                             section when omitted.
 */
const Dropdown = ({
  options,
  selectedFilters,
  haveOnSelectChange,
  onSelectChange,
  enableMax,
  maxSelect,
  avoidSelectIfMaxSelected,
  selectAllText,
  placeholder,
  valueKey,
  labelKey,
  multiple,
  keepOpen,
  includeSelectAll,
  includeFilter,
  filterPlaceholder,
  manySelectedPlaceholder,
  allSelectedPlaceholder,
  height,
  numberDisplayed,
  enableOnly,
  enableOnlyText,
  render,
  filterName,
  moduleName,
}) => {
  const resolvedValueKey = valueKey || "id";

  const { containerRef, onClose, trackChange } = useFilterTracking({
    options,
    includeSelectAll,
    filterName,
    moduleName,
    labelKey: labelKey || "name",
    selected: selectedFilters,
    valueKey: resolvedValueKey,
  });

  const onChange = (value) => {
    if (haveOnSelectChange) {
      onSelectChange(value);
      trackChange(value);
      return;
    }

    if (
      Array.isArray(value) &&
      (value.length === 0 || (enableMax && avoidSelectIfMaxSelected && value.length > maxSelect))
    ) {
      return;
    }

    onSelectChange(value);
    trackChange(value);
  };

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      <Picky
        selectAllText={selectAllText}
        placeholder={placeholder}
        options={options}
        value={selectedFilters}
        onChange={onChange}
        valueKey={resolvedValueKey}
        labelKey={labelKey ? labelKey : "name"}
        multiple={multiple ?? true}
        keepOpen={keepOpen}
        includeSelectAll={includeSelectAll}
        includeFilter={includeFilter ?? true}
        filterPlaceholder={filterPlaceholder || "Search..."}
        manySelectedPlaceholder={manySelectedPlaceholder || "%s selected"}
        allSelectedPlaceholder={allSelectedPlaceholder || "%s selected"}
        dropdownHeight={height || 500}
        numberDisplayed={numberDisplayed}
        enableOnly={enableOnly}
        enableOnlyText={enableOnlyText}
        className={enableOnly && "withOnly"}
        render={render}
        onClose={onClose}
      />
    </div>
  );
};

Dropdown.propTypes = {
  selectAllText: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  enableOnly: PropTypes.bool,
  enableOnlyText: PropTypes.string,
  numberDisplayed: PropTypes.number,
  maxSelect: PropTypes.number,
  includeSelectAll: PropTypes.bool,
  enableMax: PropTypes.bool,
  selectedFilters: PropTypes.any,
  onSelectChange: PropTypes.func,
  haveOnSelectChange: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  avoidSelectIfMaxSelected: PropTypes.bool,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  manySelectedPlaceholder: PropTypes.string,
  allSelectedPlaceholder: PropTypes.string,
  includeFilter: PropTypes.bool,
  multiple: PropTypes.bool,
  keepOpen: PropTypes.bool,
  filterName: PropTypes.string,
  moduleName: PropTypes.string,
};

Dropdown.defaultProps = {
  multiple: true,
  keepOpen: true,
};

export default Dropdown;
